import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

// Güvenilir kopyalama fonksiyonu - hem modern API hem de fallback
const copyToClipboard = async (text) => {
  if (!text) return false;
  
  // Modern Clipboard API (HTTPS gerektirir)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed:', err);
    }
  }
  
  // Fallback: execCommand (eski tarayıcılar ve HTTP için)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) return true;
  } catch (err) {
    console.warn('execCommand fallback failed:', err);
  }
  
  // Son çare: prompt ile göster
  try {
    window.prompt('Kopyalamak için Ctrl+C basın:', text);
    return true;
  } catch (err) {
    console.error('All copy methods failed:', err);
  }
  
  return false;
};

function DepositPage() {
  const navigate = useNavigate();
  const [method, setMethod] = useState('IBAN');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDocumentWarning, setShowDocumentWarning] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [approvedDeposits, setApprovedDeposits] = useState([]);
  // Çoklu Banka Hesapları Desteği
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  // Kopyalama durumu için state
  const [copiedField, setCopiedField] = useState(null);
  const [bankInfo, setBankInfo] = useState({
    bank_name: 'GARANTİ BANKASI',
    account_holder: 'TRADING PLATFORM A.Ş.',
    iban: 'TR12 3456 7890 1234 5678 9012 34'
  });
  const [cryptoInfo, setCryptoInfo] = useState(null);

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { 'Authorization': `Bearer ${token}` }
  };

  // Kullanıcı bilgilerini ve banka bilgilerini al
  useEffect(() => {
    // TÜM API çağrılarını PARALEL yap - daha hızlı yükleme
    const loadAllData = async () => {
      try {
        const [
          userRes,
          bankAccountsRes,
          bankInfoRes,
          cryptoInfoRes,
          depositsRes
        ] = await Promise.all([
          axios.get(`${API}/auth/me`, axiosConfig).catch(() => ({ data: null })),
          axios.get(`${API}/bank-accounts/active`).catch(() => ({ data: [] })),
          axios.get(`${API}/bank-info`).catch(() => ({ data: null })),
          axios.get(`${API}/crypto-info`).catch(() => ({ data: { enabled: false } })),
          axios.get(`${API}/my-deposits`, axiosConfig).catch(() => ({ data: [] }))
        ]);

        // User
        if (userRes.data) setUser(userRes.data);

        // Bank Accounts
        const banks = bankAccountsRes.data || [];
        setBankAccounts(banks);
        if (banks.length > 0) setSelectedBank(banks[0]);

        // Bank Info (fallback)
        if (bankInfoRes.data) setBankInfo(bankInfoRes.data);

        // Crypto Info
        if (cryptoInfoRes.data?.enabled) setCryptoInfo(cryptoInfoRes.data);

        // Deposits (tek çağrıdan hem pending hem approved al)
        const allDeposits = depositsRes.data || [];
        setPendingDeposits(allDeposits.filter(d => d.status === 'PENDING'));
        setApprovedDeposits(allDeposits.filter(d => d.status === 'APPROVED'));

      } catch (err) {
        console.error('Veri yüklenirken hata:', err);
      }
    };

    loadAllData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      setModalMessage('Lütfen geçerli bir miktar girin');
      setShowErrorModal(true);
      return;
    }

    setLoading(true);
    
    // Kredi kartı için özel davranış
    if (method === 'CARD') {
      setLoading(false);
      setShowModal(true);
      return;
    }

    try {
      await axios.post(`${API}/deposit`, {
        amount: parseFloat(amount),
        method: method
      }, axiosConfig);

      setModalMessage('Talebiniz alındı! Kontrol edildikten sonra hesabınıza yansıyacaktır.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Deposit error:', err);
      let errorMessage = 'Talep oluşturulurken hata oluştu';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Handle validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setModalMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const closeModalAndRedirect = () => {
    setShowModal(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gray-900 text-white flex flex-col">
      <div className="flex-1 flex items-start justify-center p-3 sm:p-4 pt-4 overflow-auto">
        <div className="w-full max-w-lg mx-auto">
          <div className="card bg-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-lg sm:text-xl font-bold">Yatırım Yap</h1>
              <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white text-lg">
                ✕
              </button>
            </div>

            <div className="mb-3">
              <label className="block text-gray-300 mb-1.5 text-xs font-semibold">Yatırım Yöntemi Seçin</label>
              <div className={`grid ${cryptoInfo ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
                <MethodButton
                  active={method === 'IBAN'}
                  onClick={() => setMethod('IBAN')}
                  icon="🏦"
                  title="IBAN"
                />
                <MethodButton
                  active={method === 'CARD'}
                  onClick={() => setMethod('CARD')}
                  icon="💳"
                  title="Kart"
                />
                {cryptoInfo && (
                  <MethodButton
                    active={method === 'CRYPTO'}
                    onClick={() => setMethod('CRYPTO')}
                    icon="₿"
                    title="Kripto"
                  />
                )}
              </div>
            </div>

            {/* IBAN Method */}
            {method === 'IBAN' && (
              <div className="bg-gray-700 p-4 rounded-lg mb-3">
                <h3 className="font-bold mb-3 text-base">🏦 Banka Seçin</h3>
                
                {/* Banka Logoları Listesi - Büyük Logolar */}
                {bankAccounts.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                      {bankAccounts.map((bank) => (
                        <button
                          key={bank.id}
                          onClick={() => setSelectedBank(bank)}
                          className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center justify-center ${
                            selectedBank?.id === bank.id
                              ? 'border-green-500 bg-green-900/40 ring-2 ring-green-400 shadow-lg shadow-green-500/20'
                              : 'border-gray-600 bg-gray-800 hover:border-gray-400 hover:bg-gray-750'
                          }`}
                        >
                          {/* Büyük Logo */}
                          {bank.bank_logo ? (
                            <img 
                              src={bank.bank_logo} 
                              alt={bank.bank_name}
                              className="w-16 h-16 object-contain rounded-lg mb-2"
                              onError={(e) => { 
                                e.target.style.display = 'none'; 
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; 
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-16 h-16 rounded-lg items-center justify-center font-bold text-white text-xl shadow-lg mb-2 ${bank.bank_logo ? 'hidden' : 'flex'}`}
                            style={{ backgroundColor: bank.bank_color || '#333' }}
                          >
                            {bank.bank_short || bank.bank_name?.substring(0, 2).toUpperCase()}
                          </div>
                          {/* Banka İsmi */}
                          <span className="text-sm text-center text-white font-semibold">{bank.bank_name}</span>
                        </button>
                      ))}
                    </div>

                    {/* Seçilen Banka Detayları */}
                    {selectedBank && (
                      <div className="bg-gray-800 p-4 rounded-xl border-2 border-green-500/50 shadow-lg">
                        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-700">
                          {/* Seçilen Banka Logo */}
                          {selectedBank.bank_logo ? (
                            <img 
                              src={selectedBank.bank_logo} 
                              alt={selectedBank.bank_name}
                              className="w-12 h-12 object-contain rounded-lg"
                            />
                          ) : (
                            <div 
                              className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-white text-lg"
                              style={{ backgroundColor: selectedBank.bank_color || '#333' }}
                            >
                              {selectedBank.bank_short || selectedBank.bank_name?.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <span className="font-bold text-green-400 text-lg">{selectedBank.bank_name}</span>
                            <p className="text-gray-400 text-xs">Aşağıdaki bilgileri kullanarak transfer yapın</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {/* Hesap Sahibi */}
                          <div className="bg-gray-900/50 p-3 rounded-lg">
                            <span className="text-gray-400 text-xs block mb-1">Hesap Sahibi</span>
                            <div className="flex justify-between items-center">
                              <span className="font-mono text-sm font-bold text-white">{selectedBank.account_holder}</span>
                              <button
                                onClick={async () => {
                                  const success = await copyToClipboard(selectedBank.account_holder);
                                  if (success) {
                                    setCopiedField('account_holder');
                                    setTimeout(() => setCopiedField(null), 2000);
                                  }
                                }}
                                className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                                  copiedField === 'account_holder' 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                              >
                                {copiedField === 'account_holder' ? '✓ Kopyalandı' : '📋 Kopyala'}
                              </button>
                            </div>
                          </div>
                          
                          {/* IBAN */}
                          <div className="bg-gray-900/50 p-3 rounded-lg">
                            <span className="text-gray-400 text-xs block mb-1">IBAN</span>
                            <div className="flex justify-between items-center">
                              <span className="font-mono font-bold text-green-400 text-sm">{selectedBank.iban}</span>
                              <button
                                onClick={async () => {
                                  const success = await copyToClipboard(selectedBank.iban);
                                  if (success) {
                                    setCopiedField('iban');
                                    setTimeout(() => setCopiedField(null), 2000);
                                  }
                                }}
                                className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                                  copiedField === 'iban' 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-green-600 hover:bg-green-700'
                                }`}
                              >
                                {copiedField === 'iban' ? '✓ Kopyalandı' : '📋 Kopyala'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Aktif banka hesabı yoksa uyarı mesajı */
                  <div className="bg-red-900/30 border-2 border-red-600 rounded-xl p-6 text-center">
                    <div className="text-5xl mb-3">⚠️</div>
                    <p className="text-red-400 text-lg font-bold">Aktif Banka Hesabı Bulunamadı!</p>
                    <p className="text-gray-400 text-sm mt-2">Para yatırma işlemi için lütfen yönetici ile iletişime geçin.</p>
                    <p className="text-gray-500 text-xs mt-3">📞 Destek: support@trading.com</p>
                  </div>
                )}
                
                {/* Ad Soyad Uyarısı - Sadece banka seçiliyse göster */}
                {selectedBank && (
                  <div className="mt-3 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
                    <div className="font-bold text-sm mb-2 text-yellow-400">
                      ⚠️ ÖNEMLİ: AÇIKLAMAYA AD SOYADI YAZINIZ
                    </div>
                    <div className="flex justify-between items-center bg-yellow-900/30 p-2 rounded">
                      <span className="text-gray-300 text-sm">Açıklama:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-white">{user?.full_name || 'Ad Soyad'}</span>
                        <button
                          onClick={async () => {
                            const success = await copyToClipboard(user?.full_name || '');
                            if (success) {
                              setCopiedField('fullname');
                              setTimeout(() => setCopiedField(null), 2000);
                            }
                          }}
                          className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                            copiedField === 'fullname' 
                              ? 'bg-green-600 text-white' 
                              : 'bg-yellow-600 hover:bg-yellow-700'
                          }`}
                        >
                          {copiedField === 'fullname' ? '✓' : '📋'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Card Method */}
            {method === 'CARD' && (
              <div className="bg-gray-700 p-3 rounded-lg mb-3">
                <h3 className="font-bold mb-2 text-sm">Kredi Kartı Bilgileri</h3>
                <div className="space-y-2">
                  <input type="text" placeholder="Kart Numarası" className="w-full bg-gray-800 border-gray-600 text-sm py-2" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="MM/YY" className="bg-gray-800 border-gray-600 text-sm py-2" />
                    <input type="text" placeholder="CVV" className="bg-gray-800 border-gray-600 text-sm py-2" />
                  </div>
                  <input type="text" placeholder="Kart Sahibi Adı" className="w-full bg-gray-800 border-gray-600 text-sm py-2" />
                </div>
                <div className="mt-2 p-2 bg-blue-900/30 border border-blue-700 rounded text-xs">
                  🔒 Güvenli ödeme alt yapısı
                </div>
              </div>
            )}

            {/* CRYPTO Method */}
            {method === 'CRYPTO' && cryptoInfo && (
              <div className="bg-gray-700 p-3 rounded-lg mb-3">
                <h3 className="font-bold mb-2 text-sm">₿ Kripto ile Yatırım</h3>
                
                <div className="bg-blue-900/30 border border-blue-700 rounded p-2 mb-2">
                  <div className="text-xs text-blue-300">📌 Kripto Türü:</div>
                  <div className="text-white text-sm font-bold">{cryptoInfo.crypto_type}</div>
                </div>

                <div className="space-y-2">
                  <div className="bg-gray-800 p-2 rounded">
                    <div className="text-xs text-gray-400 mb-1">Cüzdan Adresi:</div>
                    <div className="flex items-center gap-1">
                      <code className="flex-1 bg-gray-900 px-2 py-1 rounded text-[10px] font-mono text-green-400 break-all">
                        {cryptoInfo.wallet_address}
                      </code>
                      <button
                        type="button"
                        onClick={async () => {
                          const success = await copyToClipboard(cryptoInfo.wallet_address);
                          if (success) {
                            setCopiedField('wallet');
                            setTimeout(() => setCopiedField(null), 2000);
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs whitespace-nowrap transition-all ${
                          copiedField === 'wallet' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {copiedField === 'wallet' ? '✓' : '📋'}
                      </button>
                    </div>
                  </div>

                  {cryptoInfo.qr_code && (
                    <div className="bg-gray-800 p-2 rounded text-center">
                      <div className="text-xs text-gray-400 mb-1">QR Kod:</div>
                      <img
                        src={cryptoInfo.qr_code}
                        alt="QR Code"
                        className="mx-auto max-w-[120px] rounded"
                        onError={(e) => {
                          e.target.src = 'https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=' + encodeURIComponent(cryptoInfo.wallet_address);
                        }}
                      />
                    </div>
                  )}

                  <div className="bg-yellow-900/30 border border-yellow-700 rounded p-2 text-xs">
                    <div className="font-semibold text-yellow-400 mb-1">⚠️ Önemli:</div>
                    <ul className="space-y-0.5 text-gray-300 text-[10px]">
                      <li>• Doğru cüzdan adresine transfer yapın</li>
                      <li>• Yanlış ağdan gönderim = para kaybı!</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-gray-300 mb-1 text-xs">Yatırılacak Miktar ({user?.account_type === 'VIP' ? '$' : 'TL'})</label>
                <input
                  type="number"
                  step="0.01"
                  min="10"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Minimum 10 TL"
                  className="w-full text-sm py-2"
                  required
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary py-2 text-sm"
                >
                  {loading ? 'Gönderiliyor...' : 'Talep Oluştur'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex-1 btn-secondary py-2 text-sm"
                >
                  İptal
                </button>
              </div>
            </form>
            
            {/* Bekleyen Yatırım Talepleri - Formun hemen altında */}
            {pendingDeposits.length > 0 && (
              <div className="mt-4 bg-yellow-900/30 border border-yellow-600 rounded-xl p-3">
                <h3 className="text-sm font-bold text-yellow-400 mb-2">⏳ Bekleyen Talepler ({pendingDeposits.length})</h3>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {pendingDeposits.map((deposit) => (
                    <div key={deposit.id} className="bg-gray-800 rounded-lg p-2 flex items-center justify-between">
                      <div>
                        <div className="text-xs text-gray-400">#{deposit.id?.slice(0, 8)}</div>
                        <div className="text-sm font-bold text-white">
                          {user?.account_type === 'VIP' ? `$${deposit.amount}` : `₺${deposit.amount}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="px-2 py-1 text-xs bg-yellow-600 rounded-full">{deposit.method || 'IBAN'}</span>
                        <div className="text-[10px] text-gray-400 mt-1">
                          {deposit.created_at ? new Date(deposit.created_at).toLocaleDateString('tr-TR') : '-'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onaylanan Yatırımlar - Her zaman göster */}
            <div className="mt-4 bg-green-900/30 border border-green-600 rounded-xl p-3">
              <h3 className="text-sm font-bold text-green-400 mb-2">✅ Onaylanan Yatırımlar ({approvedDeposits.length})</h3>
              {approvedDeposits.length > 0 ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {approvedDeposits.map((deposit) => (
                    <div key={deposit.id} className="bg-gray-800 rounded-lg p-2 flex items-center justify-between">
                      <div>
                        <div className="text-xs text-gray-400">#{deposit.id?.slice(0, 8)}</div>
                        <div className="text-sm font-bold text-green-400">
                          +{user?.account_type === 'VIP' ? `$${deposit.amount?.toLocaleString()}` : `₺${deposit.amount?.toLocaleString()}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="px-2 py-1 text-xs bg-green-600 rounded-full">{deposit.method || 'IBAN'}</span>
                        <div className="text-[10px] text-gray-400 mt-1">
                          {deposit.updated_at ? new Date(deposit.updated_at).toLocaleDateString('tr-TR') : 
                           deposit.created_at ? new Date(deposit.created_at).toLocaleDateString('tr-TR') : '-'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-400 text-sm py-3">
                  Henüz onaylanan yatırım talebi bulunmuyor
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal - Kredi Kartı Uyarısı */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border-2 border-red-500/50 animate-fade-in">
            <div className="p-6 text-center">
              {/* İkon */}
              <div className="mx-auto w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mb-4 border-4 border-red-500">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Başlık */}
              <h3 className="text-2xl font-bold text-white mb-3">Ödeme Yöntemi Kullanılamıyor</h3>
              
              {/* Mesaj */}
              <p className="text-gray-300 text-base mb-6 leading-relaxed">
                Şu anda bu ödeme yöntemi kullanılamıyor. Lütfen diğer ödeme yöntemlerini deneyiniz.
              </p>
              
              {/* Buton */}
              <button
                onClick={closeModalAndRedirect}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg"
              >
                Anladım, Ana Sayfaya Dön
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 via-gray-850 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-green-500/30 animate-fade-in">
            <div className="p-8 text-center">
              <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6 border-4 border-green-500 animate-pulse">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Başarılı!</h3>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">{modalMessage}</p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 shadow-lg text-lg"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 via-gray-850 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-red-500/30 animate-fade-in">
            <div className="p-8 text-center">
              <div className="mx-auto w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6 border-4 border-red-500">
                <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Uyarı!</h3>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">{modalMessage}</p>
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 shadow-lg text-lg"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MethodButton({ active, onClick, icon, title }) {
  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg border-2 transition-all ${
        active
          ? 'border-red-500 bg-red-900/20'
          : 'border-gray-600 bg-gray-700 hover:border-gray-500'
      }`}
    >
      <div className="text-xl mb-0.5">{icon}</div>
      <div className="text-[10px] font-semibold">{title}</div>
    </button>
  );
}

export default DepositPage;
